import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import {
  Box, Card, CardContent, Typography, TextField, Button, Grid,
  FormControl, CircularProgress, Alert, Divider, Paper, Chip, 
  Accordion, AccordionSummary, AccordionDetails, LinearProgress, Select, MenuItem,
  Radio, RadioGroup, FormControlLabel, Dialog, DialogTitle, DialogContent, 
  DialogActions, Badge, Slider, AppBar, Toolbar, IconButton
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import HistoryIcon from '@mui/icons-material/History';
import UpdateIcon from '@mui/icons-material/Update';
import PersonIcon from '@mui/icons-material/Person';
import QuizIcon from '@mui/icons-material/Quiz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function EmployeeTrainingView() {
  const location = useLocation();
  const [employeeId, setEmployeeId] = useState("");
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showTrainings, setShowTrainings] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [relatedVideos, setRelatedVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [updateHistory, setUpdateHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [activeTab, setActiveTab] = useState("videos");
  const [form, setForm] = useState({
    employee_id: ""
  });
  const [progressUpdate, setProgressUpdate] = useState({
    status: "",
    progressPercentage: 0,
    completionNotes: "",
    completionDate: null
  });
  
  const [testQuestions, setTestQuestions] = useState([]);
  const [loadingTest, setLoadingTest] = useState(false);
  const [testAnswers, setTestAnswers] = useState({});
  const [showTest, setShowTest] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testSubmitted, setTestSubmitted] = useState(false);
  const [videosCompleted, setVideosCompleted] = useState(false);
  const [retakingTest, setRetakingTest] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [filteredQuestions, setFilteredQuestions] = useState([]);

  const statusOptions = ["Not Started", "In Progress", "Completed", "Deferred"];

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess("");
        setError("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  useEffect(() => {
    const fetchEmployeeId = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/user/current`, {withCredentials: true});
        const employeeIdFromSession = response.data.employee_id || "";
        setForm(prevForm => ({
          ...prevForm,
          employee_id: employeeIdFromSession
        }));
        setEmployeeId(employeeIdFromSession);
        
        // Automatically fetch trainings when employee ID is loaded
        if (employeeIdFromSession) {
          fetchTrainings(employeeIdFromSession);
        }
      } catch (err) {
        //console.error("Failed to fetch employee ID from session:", err);
        setError("Failed to load employee information");
      }
    };
  
    fetchEmployeeId();
  }, []);

  useEffect(() => {
    const targetTrainingId = location.state?.selectedTrainingId;
    if (targetTrainingId && trainings.length > 0) {
      const targetTraining = trainings.find(t => t.id === targetTrainingId);
      if (targetTraining) {
        setSelectedTraining(targetTraining);
      }
    }
  }, [trainings, location.state]);

  useEffect(() => {
    if (selectedTraining && selectedTraining.skillCategory && selectedTraining.skillContent) {
      fetchRelatedVideos(selectedTraining.skillCategory, selectedTraining.skillContent);
      fetchUpdateHistory(selectedTraining.id);
      fetchTestQuestions(selectedTraining.skillCategory, selectedTraining.skillContent);
      checkTestStatus(selectedTraining.id);
    }
  }, [selectedTraining]);

  useEffect(() => {
    if (selectedTraining && testQuestions.length > 0) {
      const filtered = testQuestions.filter(question => 
        question.skillContent === selectedTraining.skillContent
      );
      setFilteredQuestions(filtered);
      setCurrentQuestionIndex(0);
    }
  }, [testQuestions, selectedTraining]);

  const fetchTrainings = async (empId = employeeId) => {
    if (!empId.trim()) {
      setError("Employee ID not available");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await axios.get(
        `${API_URL}/api/trainings/employee/id/${empId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          withCredentials: true
        }
      );
      
      if (response.data.length === 0) {
        setError("No trainings found for this employee ID");
        setShowTrainings(false);
      } else {
        setTrainings(response.data);
        setShowTrainings(true);
      }
    } catch (error) {
      //console.error("Error fetching trainings:", error);
      setError(error.response?.data?.message || "Failed to fetch trainings");
      setShowTrainings(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchUpdateHistory = async (trainingId) => {
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await axios.get(
        `${API_URL}/api/trainings/${trainingId}/history`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
           
          }
        }
      );
      setUpdateHistory(response.data);
    } catch (error) {
      //console.error("Error fetching update history:", error);
      setUpdateHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchRelatedVideos = async (category, content) => {
    setLoadingVideos(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await axios.get(
        `${API_URL}/api/training-videos/category/${category}/content/${content}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          
          }
        }
      );
      
      const formattedVideos = response.data.map(video => ({
        id: video.id,
        title: video.title,
        description: video.description || "Training video for " + video.skillContent,
        url: video.videoUrl
      }));
      
      setRelatedVideos(formattedVideos);
    } catch (error) {
      //console.error("Error fetching related videos:", error);
      if (error.response && error.response.status === 404) {
        setRelatedVideos([]);
      } else {
        setError("Failed to fetch training videos");
        setRelatedVideos([]);
      }
    } finally {
      setLoadingVideos(false);
    }
  };
  
  const handleTakeTest = () => {
    setTestAnswers({});
    setTestSubmitted(false);
    setShowTest(true);
    setRetakingTest(testSubmitted);
    setCurrentQuestionIndex(0);
    
    if (filteredQuestions.length === 0 && selectedTraining) {
      fetchTestQuestions(selectedTraining.skillCategory, selectedTraining.skillContent);
    }
  };

  const fetchTestQuestions = async (category, content) => {
    setLoadingTest(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await axios.get(
        `${API_URL}/api/test-questions`,
        { 
          params: { 
            skillCategory: category, 
            skillContent: content 
          },
          
            headers: {
              Authorization: `Bearer ${token}`,
             
            }
          
        }
      );
      
      if (response.data && Array.isArray(response.data)) {
        const formattedQuestions = response.data.map(q => {
          const options = {};
          if (q.optionA) options.A = q.optionA;
          if (q.optionB) options.B = q.optionB;
          if (q.optionC) options.C = q.optionC;
          if (q.optionD) options.D = q.optionD;
          
          return {
            id: q.id,
            question: q.question,
            options: options,
            correctAnswer: q.correctAnswer,
            skillContent: q.skillContent,
            skillCategory: q.skillCategory,
            ...q
          };
        });
        
        setTestQuestions(formattedQuestions);
      } else {
        //console.error("Invalid test questions data format:", response.data);
        setTestQuestions([]);
      }
    } catch (error) {
      //console.error("Error fetching test questions:", error);
      setTestQuestions([]);
    } finally {
      setLoadingTest(false);
    }
  };

  const checkTestStatus = async (trainingId) => {
    if (!trainingId || !employeeId) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await axios.get(
        `${API_URL}/api/test-results/training/${trainingId}/employee/${employeeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
       
          }
        }
      );
      
      
      if (response.data && response.data.result) {
        setTestResult(response.data);
        setTestSubmitted(true);
        if (response.data.passed) {
          setVideosCompleted(true);
        }
      } else {
        setTestResult(null);
        setTestSubmitted(false);
      }
    } catch (error) {
      //console.error("Error checking test status:", error);
      setTestResult(null);
      setTestSubmitted(false);
    }
  };

  const handleTrainingSelect = (training) => {
    setSelectedTraining(training);
    setProgressUpdate({
      status: training.status || "Not Started",
      progressPercentage: training.progressPercentage || 0,
      completionNotes: training.completionNotes || "",
      completionDate: training.completionDate || null
    });
    
    setTestAnswers({});
    setTestSubmitted(false);
    setTestResult(null);
    setShowTest(false);
    setVideosCompleted(training.progressPercentage >= 50);
    setActiveTab("videos");
    setRetakingTest(false);
    setCurrentQuestionIndex(0);
  };

  const handleProgressChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "status" && value === "Completed") {
      setProgressUpdate(prev => ({
        ...prev,
        [name]: value,
        completionDate: new Date().toISOString().split('T')[0]
      }));
    } else {
      setProgressUpdate(prev => ({
        ...prev,
        [name]: name === "progressPercentage" ? parseInt(value) : value
      }));
    }
  };

  const updateTrainingProgress = async () => {
    if (!selectedTraining) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const updateData = {
        ...progressUpdate,
        employeeId: employeeId,
        employeeName: selectedTraining.employee || "Employee", 
        updateDate: new Date().toISOString()
      };
      
      const response = await axios.put(
        `${API_URL}/api/trainings/${selectedTraining.id}/progress`, 
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      const updatedTrainings = trainings.map(training => 
        training.id === selectedTraining.id ? { ...training, ...progressUpdate } : training
      );
      setTrainings(updatedTrainings);
      setSelectedTraining({ ...selectedTraining, ...progressUpdate });
      
      fetchUpdateHistory(selectedTraining.id);
      
      setSuccess("Training progress updated successfully!");
    } catch (error) {
      //console.error("Error updating training progress:", error);
      setError(error.response?.data?.message || "Failed to update training progress");
    }
  };

  const handleCloseTest = () => {
    setShowTest(false);
  };

  const handleAnswerChange = (questionId, answer) => {
    setTestAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };
  
  const handleNextQuestion = () => {
    if (currentQuestionIndex < filteredQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const isQuestionAnswered = (questionId) => {
    return testAnswers[questionId] !== undefined;
  };

  const handleSubmitTest = async () => {
    if (!selectedTraining) {
      setError("No training selected");
      return;
    }
  
    if (filteredQuestions.length === 0) {
      setError("No questions available for this test");
      return;
    }
  
    const answeredQuestions = Object.keys(testAnswers).length;
    if (answeredQuestions !== filteredQuestions.length) {
      setError(`Please answer all questions before submitting. ${answeredQuestions}/${filteredQuestions.length} questions answered.`);
      return;
    }
  
    try {
      let correctAnswers = 0;
      for (const question of filteredQuestions) {
        const userAnswer = testAnswers[question.id];
        if (userAnswer === question.correctAnswer) {
          correctAnswers++;
        }
      }
      const clientScore = Math.round((correctAnswers / filteredQuestions.length) * 100);
   
      const testSubmission = {
        trainingId: selectedTraining.id,
        employeeId: employeeId,
        answers: testAnswers,
        submissionDate: new Date().toISOString(),
        skillCategory: selectedTraining.skillCategory,
        skillContent: selectedTraining.skillContent
      };
  
  
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await axios.post(
        `${API_URL}/api/test-questions/submit`,
        testSubmission,
        { 
         
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          timeout: 10000
        }
      );
  
  
      let result;
      if (response.data && typeof response.data === 'object') {
        result = {
          passed: response.data.passed === true,
          score: response.data.score !== undefined ? response.data.score : clientScore,
          feedback: response.data.feedback || ""
        };
      } else {
        result = {
          passed: clientScore >= 70,
          score: clientScore,
          feedback: clientScore >= 70 ? 
            "Congratulations! You passed the test." : 
            "You did not pass. Please review the material and try again."
        };
      }
  
      
      setTestResult(result);
      setTestSubmitted(true);
      setShowTest(false);
  
      if (result.passed && selectedTraining.status !== "Completed") {
        const today = new Date().toISOString().split('T')[0];
        const updateData = {
          status: "Completed",
          progressPercentage: 100,
          completionNotes: `Test passed on ${today}. Training completed.`,
          completionDate: today,
          employeeId: employeeId,
          employeeName: selectedTraining.employee || "Employee",
          updateDate: new Date().toISOString()
        };
  
        try {
          const token = localStorage.getItem('token');
          if (!token) {
            alert("No authentication token found. Please log in.");
            return;
          }
  
          const payload = JSON.parse(atob(token.split('.')[1]));
          const company_id = payload.company_id;
          
          await axios.put(
            `${API_URL}/api/trainings/${selectedTraining.id}/progress`, 
            updateData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json"
              }
            }
          );
  
          const updatedTrainings = trainings.map(training => 
            training.id === selectedTraining.id ? { ...training, ...updateData } : training
          );
          setTrainings(updatedTrainings);
          setSelectedTraining({ ...selectedTraining, ...updateData });
          setProgressUpdate(updateData);
          
          fetchUpdateHistory(selectedTraining.id);
          
          setSuccess("Congratulations! You passed the test and completed the training.");
        } catch (updateError) {
          //console.error("Error updating training progress:", updateError);
          setSuccess("Test passed successfully! However, there was an issue updating your training progress. Please try refreshing the page.");
        }
      } else if (result.passed) {
        setSuccess("Test passed successfully!");
      } else {
        setError("You did not pass the test. Please review the material and try again.");
      }
    } catch (error) {
      //console.error("Error submitting test:", error);
      
      if (error.code === 'ECONNABORTED') {
        setError("Request timed out. Please check your connection and try again.");
        return;
      }
      
      if (error.response) {
        //console.error("Error status:", error.response.status);
        //console.error("Error data:", error.response.data);
        
        if (error.response.status === 404) {
          setError("The test submission endpoint could not be found. Please contact IT support or try again later.");
        } else if (error.response.status === 401 || error.response.status === 403) {
          setError("You are not authorized to submit this test. Please log in again.");
        } else {
          setError(error.response.data?.message || `Server error (${error.response.status}). Please try again later.`);
        }
      } else if (error.request) {
        //console.error("No response received:", error.request);
        setError("No response from server. Please check your connection and try again.");
      } else {
        setError(`Failed to submit test: ${error.message || "Unknown error"}`);
      }
    }
  };

  const handleRetakeTest = () => {
    setTestSubmitted(false);
    setTestResult(null);
    setTestAnswers({});
    setShowTest(true);
    setRetakingTest(true);
    setCurrentQuestionIndex(0);
  };

  const getStatusChipColor = (status) => {
    switch (status) {
      case "Completed": return "success";
      case "In Progress": return "primary";
      case "Deferred": return "warning";
      default: return "default";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleString();
  };

  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const YouTubeEmbed = ({ videoUrl }) => {
    const videoId = getYouTubeVideoId(videoUrl);
    
    if (!videoId) return null;
    
    return (
      <Box sx={{ width: '100%', height: 0, paddingBottom: '56.25%', position: 'relative', mb: 3 }}>
        <iframe
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderRadius: '8px'
          }}
          src={`https://www.youtube.com/embed/${videoId}`}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </Box>
    );
  };
 
  const handleVideosCompleted = () => {
    setVideosCompleted(true);
    setActiveTab("test");
    
    if (progressUpdate.progressPercentage < 50) {
      const updatedProgress = {
        ...progressUpdate,
        progressPercentage: 50,
        status: "In Progress"
      };
      setProgressUpdate(updatedProgress);

      const updateData = {
        ...updatedProgress,
        employeeId: employeeId,
        employeeName: selectedTraining.employee || "Employee",
        updateDate: new Date().toISOString()
      };
      
      axios.put(
        `${API_URL}/api/trainings/${selectedTraining.id}/progress`, 
        updateData
      ).then(() => {
        const updatedTrainings = trainings.map(training => 
          training.id === selectedTraining.id ? { ...training, ...updatedProgress } : training
        );
        setTrainings(updatedTrainings);
        setSelectedTraining({ ...selectedTraining, ...updatedProgress });
      }).catch(error => {
        //console.error("Error updating progress after video completion:", error);
      });
    }
    
    setSuccess("Videos completed! Now take the assessment test to complete your training.");
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100vh', 
      overflow: 'hidden',
      bgcolor: 'white' 
    }}>
      {/* Header with page title */}
      <AppBar position="static" color="default" elevation={1} sx={{ bgcolor: 'white', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Toolbar>
        
      
          <Typography variant="h4" component="div" sx={{ flexGrow: 1, fontWeight: 'bold', }}>
        View Training
          </Typography>
       
        </Toolbar>
      </AppBar>

      {/* Subtitle */}
      <Box sx={{ px: 3, pt: 2, pb: 1 }}>
        <Typography variant="h6" color="text.secondary">
          View and Track Your Training Progress
        </Typography>
      </Box>

      <Box sx={{ 
        p: 3, 
        flexGrow: 1, 
        overflow: 'auto',
        bgcolor: 'white'
      }}>
        {success && (
          <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
            {success}
          </Alert>
        )}
       {error && (
  <Alert
    severity={
      error === "No trainings found for this employee ID"
        ? "info"
        : "error"
    }
    sx={{ width: "100%", mb: 2 }}
  >
    {error}
  </Alert>
)}

        {showTrainings && (
          <Grid container spacing={3} sx={{ height: 'calc(100% - 20px)' }}>
            <Grid item xs={12} md={4} lg={3} sx={{ height: { md: '100%' }, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <MenuBookIcon sx={{ mr: 1, color: 'primary.main' }} />
                My Trainings
               
              </Typography>
              <Paper sx={{ 
                flexGrow: 1, 
                overflow: "auto", 
                maxHeight: { xs: '400px', md: 'none' }, 
                boxShadow: 3,
                borderRadius: 2
              }}>
                {trainings.map((training) => (
                  <Box
                    key={training.id}
                    sx={{
                      p: 2,
                      mb: 1,
                      cursor: "pointer",
                      borderLeft: selectedTraining?.id === training.id ? "4px solid #1976d2" : "none",
                      bgcolor: selectedTraining?.id === training.id ? "rgba(25, 118, 210, 0.08)" : "transparent",
                      "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
                      transition: 'all 0.2s'
                    }}
                    onClick={() => handleTrainingSelect(training)}
                  >
                    <Typography variant="subtitle1" fontWeight="bold" noWrap>
                      {training.title}
                    </Typography>
                    <Grid container spacing={1} sx={{ mt: 1 }}>
                      <Grid item>
                        <Chip 
                          size="small" 
                          label={training.status || "Not Started"} 
                          color={getStatusChipColor(training.status)} 
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item>
                        <Chip 
                          size="small" 
                          label={`${training.progressPercentage || 0}% Complete`} 
                          variant="outlined" 
                        />
                      </Grid>
                    </Grid>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {formatDate(training.startDate)} - {formatDate(training.endDate)}
                    </Typography>
                    {training.completionDate && (
                      <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
                        Completed: {formatDate(training.completionDate)}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Paper>
            </Grid>

            <Grid item xs={12} md={8} lg={9} sx={{ height: { md: '100%' }, display: 'flex', flexDirection: 'column' }}>
              {selectedTraining ? (
                <Card sx={{ 
                  boxShadow: 3, 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  borderRadius: 2
                }}>
                  <CardContent sx={{ 
                    flexGrow: 1, 
                    overflow: 'auto',
                    p: 3
                  }}>
                    <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                      {selectedTraining.title}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
                      {(selectedTraining.videoUrl || relatedVideos.length > 0 || loadingVideos) && (
                        <Button
                          variant={activeTab === "videos" ? "contained" : "outlined"}
                          color="primary"
                          onClick={() => setActiveTab("videos")}
                          startIcon={<OndemandVideoIcon />}
                          size="large"
                          sx={{ minWidth: 180 }}
                        >
                          Training Videos
                        </Button>
                      )}
                      {(filteredQuestions.length > 0 || loadingTest) && (
                        <Button
                          variant={activeTab === "test" ? "contained" : "outlined"}
                          color="primary"
                          onClick={() => setActiveTab("test")}
                          startIcon={<QuizIcon />}
                          disabled={!videosCompleted && !testSubmitted && (selectedTraining.videoUrl || relatedVideos.length > 0)}
                          size="large"
                          sx={{ minWidth: 180 }}
                        >
                          Assessment Test
                          {testResult?.passed && (
                            <CheckCircleIcon color="success" fontSize="small" sx={{ ml: 1 }} />
                          )}
                        </Button>
                      )}
                    </Box>

                    <Grid container spacing={4}>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 4 }}>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Start Date
                              </Typography>
                              <Typography variant="body1" fontWeight="medium">
                                {formatDate(selectedTraining.startDate)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                End Date
                              </Typography>
                              <Typography variant="body1" fontWeight="medium">
                                {formatDate(selectedTraining.endDate)}
                              </Typography>
                            </Grid>
                            {selectedTraining.status === "Completed" && selectedTraining.completionDate && (
                              <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                  Completion Date
                                </Typography>
                                <Typography variant="body1" color="success.main" fontWeight="medium">
                                  {formatDate(selectedTraining.completionDate)}
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                        
                        <Box sx={{ mb: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            Trainer
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {selectedTraining.trainer}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ mb: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            Skill Category
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {selectedTraining.skillCategory}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ mb: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            Skill Content
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {selectedTraining.skillContent}
                          </Typography>
                        </Box>

                        {activeTab === "videos" && (selectedTraining.videoUrl || relatedVideos.length > 0 || loadingVideos) && (
                          <>
                            {selectedTraining.videoUrl && (
                              <Box sx={{ mb: 4 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'medium' }}>
                                  Main Training Video
                                </Typography>
                                <YouTubeEmbed videoUrl={selectedTraining.videoUrl} />
                              </Box>
                            )}
                            
                            <Box sx={{ mb: 4 }}>
                              <Typography variant="subtitle1" gutterBottom fontWeight="bold" sx={{ color: 'primary.main' }}>
                                Related Training Videos
                              </Typography>
                              
                              {loadingVideos ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                  <CircularProgress size={30} />
                                </Box>
                              ) : relatedVideos.length > 0 ? (
                                <Box>
                                  {relatedVideos.map((video, index) => (
                                    <Box key={index} sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
                                      <Typography variant="subtitle1" fontWeight="bold">{video.title}</Typography>
                                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        {video.description}
                                      </Typography>
                                      <YouTubeEmbed videoUrl={video.url} />
                                    </Box>
                                  ))}
                                  
                                  <Box sx={{ mt: 4, textAlign: 'center' }}>
                                    <Button 
                                      variant="outlined" 
                                    
                                      onClick={handleVideosCompleted}
                                      disabled={videosCompleted}
                                      size="large"
                                      sx={{ px: 4, py: 1.5 }}
                                    >
                                      {videosCompleted ? "✓ Videos Completed" : "I've Completed Watching Videos"}
                                    </Button>
                                  </Box>
                                </Box>
                              ) : (
                                <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'background.paper', borderRadius: 2 }}>
                                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                                    No related videos available for this skill category.
                                  </Typography>
                                  
                                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                                    <Button 
                                      variant="contained" 
                                      color="primary"
                                      onClick={handleVideosCompleted}
                                      size="large"
                                      sx={{ px: 4, py: 1.5 }}
                                    >
                                      Continue to Assessment Test
                                    </Button>
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          </>
                        )}
                        
                        {activeTab === "test" && (filteredQuestions.length > 0 || loadingTest) && (
                          <Box sx={{ mb: 4 }}>
                            <Typography variant="subtitle1" gutterBottom fontWeight="bold" sx={{ color: 'primary.main' }}>
                              Skill Assessment Test for {selectedTraining.skillContent}
                            </Typography>
                            
                            {loadingTest ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress size={30} />
                              </Box>
                            ) : testSubmitted ? (
                              <Box sx={{ p: 4, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 2 }}>
                                <Typography variant="h6" align="center" sx={{ mb: 3 }}>
                                  {testResult && testResult.passed ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'success.main' }}>
                                      <CheckCircleIcon sx={{ mr: 1, fontSize: 40 }} />
                                      Congratulations! You Passed!
                                    </Box>
                                  ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'error.main' }}>
                                      <ErrorOutlineIcon sx={{ mr: 1, fontSize: 40 }} />
                                      You Did Not Pass
                                    </Box>
                                  )}
                                </Typography>
                                
                                <Typography variant="h3" align="center" sx={{ mb: 3, fontWeight: 'bold' }}>
                                  {testResult ? (testResult.score || 0) : 0}%
                                </Typography>
                                
                                <Typography variant="body1" align="center" sx={{ mb: 3, fontSize: '1.1rem' }}>
                                  {testResult && testResult.passed 
                                    ? "You have successfully completed this training module." 
                                    : "Please review the training materials and try again."}
                                </Typography>
                                
                                {(!testResult || !testResult.passed) && (
                                  <Button 
                                    variant="contained" 
                                    color="primary"
                                    fullWidth
                                    onClick={handleRetakeTest}
                                    size="large"
                                    sx={{ py: 1.5 }}
                                  >
                                    Retake Test
                                  </Button>
                                )}
                              </Box>
                            ) : (
                              <Box sx={{ p: 4, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 2 }}>
                                <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
                                  This assessment test will evaluate your understanding of the training material.
                                  You must score <strong>70% or higher</strong> to pass.
                                </Typography>
                                
                                <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', fontSize: '1rem' }}>
                                  The test consists of <strong>{filteredQuestions.length}</strong> multiple-choice questions.
                                </Typography>
                                
                                <Button 
                                  variant="outlined" 
                                 
                                  fullWidth
                                  onClick={handleTakeTest}
                                  size="large"
                                  sx={{ py: 1.5 }}
                                >
                                  Start Assessment Test
                                </Button>
                              </Box>
                            )}
                          </Box>
                        )}
                        
                        {!(selectedTraining.videoUrl || relatedVideos.length > 0 || loadingVideos) && !(filteredQuestions.length > 0 || loadingTest) && (
                          <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'background.paper', borderRadius: 2 }}>
                            <Typography variant="h6" color="text.secondary">
                              No videos or assessment test available for this training.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              You can mark this training as complete once you've reviewed the required materials.
                            </Typography>
                          </Box>
                        )}
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 3, mb: 3, boxShadow: 2, borderRadius: 2 }}>
                          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                            <UpdateIcon fontSize="small" sx={{ mr: 1 }} />
                            Update Your Progress
                          </Typography>
                          
                          <Grid container spacing={3}>
                            <Grid item xs={12}>
                              <FormControl fullWidth sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  Current Status
                                </Typography>
                                <Select
                                  value={progressUpdate.status}
                                  name="status"
                                  onChange={handleProgressChange}
                                  size="medium"
                                >
                                  {statusOptions.map((option) => (
                                    <MenuItem key={option} value={option}>{option}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Progress: {progressUpdate.progressPercentage}%
                              </Typography>
                              <Slider
                                value={progressUpdate.progressPercentage}
                                onChange={(e, newValue) => 
                                  setProgressUpdate(prev => ({ ...prev, progressPercentage: newValue }))
                                }
                                min={0}
                                max={100}
                                step={10}
                                marks
                                valueLabelDisplay="auto"
                                sx={{ py: 1 }}
                              />
                            </Grid>
                            
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Completion Notes"
                                name="completionNotes"
                                value={progressUpdate.completionNotes}
                                onChange={handleProgressChange}
                                variant="outlined"
                                placeholder="Add any notes about your progress..."
                              />
                            </Grid>
                            
                            <Grid item xs={12}>
                              <Button 
                                variant="outlined" 
                             
                                onClick={updateTrainingProgress}
                                fullWidth
                                size="large"
                                sx={{ py: 1.5 }}
                              >
                                Save Progress Update
                              </Button>
                            </Grid>
                          </Grid>
                        </Paper>
                        
                        <Accordion sx={{ mb: 3, boxShadow: 2, borderRadius: 2 }}>
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="panel1a-content"
                            id="panel1a-header"
                            sx={{ p: 2 }}
                          >
                            <Typography variant="h6" sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                              <HistoryIcon fontSize="small" sx={{ mr: 1 }} />
                              Progress History
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ p: 2 }}>
                            {loadingHistory ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress size={24} />
                              </Box>
                            ) : updateHistory.length > 0 ? (
                              <Box>
                                {updateHistory.map((update, index) => (
                                  <Box 
                                    key={index} 
                                    sx={{ 
                                      p: 2, 
                                      mb: 2, 
                                      bgcolor: 'background.paper', 
                                      borderRadius: 1,
                                      boxShadow: 1,
                                      border: '1px solid',
                                      borderColor: 'divider'
                                    }}
                                  >
                                    <Typography variant="subtitle2" gutterBottom>
                                      Status: {update.status}
                                    </Typography>
                                    <Typography variant="body2" gutterBottom>
                                      Progress: {update.progressPercentage}%
                                    </Typography>
                                    {update.completionNotes && (
                                      <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Notes: {update.completionNotes}
                                      </Typography>
                                    )}
                                    <Typography variant="caption" color="text.secondary">
                                      Updated: {formatDateTime(update.updateDate)}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
                                No update history available.
                              </Typography>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ) : (
                <Paper 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100%',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 3,
                    p: 5,
                    textAlign: 'center'
                  }}
                >
                  <Box>
                    <MenuBookIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                      Select a Training
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                      Choose a training program from the list on the left to view details, watch videos, and track your progress.
                    </Typography>
                  </Box>
                </Paper>
              )}
            </Grid>
          </Grid>
        )}
      </Box>
      
      <Dialog 
        open={showTest} 
        onClose={handleCloseTest}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ p: 3, bgcolor: 'primary.main', color: 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Assessment Test: {selectedTraining?.skillContent}
            </Typography>
            <Chip 
              label={`Question ${currentQuestionIndex + 1} of ${filteredQuestions.length}`}
              sx={{ bgcolor: 'white', color: 'primary.main', fontWeight: 'bold' }}
            />
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 4 }}>
          {filteredQuestions.length > 0 && currentQuestionIndex < filteredQuestions.length ? (
            <Box>
              <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 'medium' }}>
                {filteredQuestions[currentQuestionIndex].question}
              </Typography>
              
              <FormControl component="fieldset" sx={{ width: '100%', mt: 2 }}>
                <RadioGroup
                  value={testAnswers[filteredQuestions[currentQuestionIndex].id] || ''}
                  onChange={(e) => handleAnswerChange(filteredQuestions[currentQuestionIndex].id, e.target.value)}
                >
                  {Object.entries(filteredQuestions[currentQuestionIndex].options).map(([key, value]) => (
                    <Paper 
                      key={key}
                      sx={{ 
                        mb: 2,
                        border: '2px solid',
                        borderColor: testAnswers[filteredQuestions[currentQuestionIndex].id] === key ? 'primary.main' : 'divider',
                        bgcolor: testAnswers[filteredQuestions[currentQuestionIndex].id] === key ? 'primary.light' : 'transparent',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'rgba(25, 118, 210, 0.04)'
                        }
                      }}
                    >
                      <FormControlLabel 
                        value={key} 
                        control={<Radio />} 
                        label={
                          <Typography variant="body1" sx={{ py: 1.5, px: 1 }}>
                            <strong>{key}:</strong> {value}
                          </Typography>
                        }
                        sx={{ width: '100%', mx: 0, px: 2 }}
                      />
                    </Paper>
                  ))}
                </RadioGroup>
              </FormControl>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button 
                  variant="outlined"
                  onClick={handlePreviousQuestion}
                  disabled={currentQuestionIndex === 0}
                  size="large"
                  sx={{ px: 4 }}
                >
                  Previous
                </Button>
                <Box>
                  {currentQuestionIndex === filteredQuestions.length - 1 ? (
                    <Button 
                      variant="contained" 
                      color="primary"
                      onClick={handleSubmitTest}
                      disabled={!isQuestionAnswered(filteredQuestions[currentQuestionIndex].id)}
                      size="large"
                      sx={{ px: 4 }}
                    >
                      Submit Test ({Object.keys(testAnswers).length}/{filteredQuestions.length} Answered)
                    </Button>
                  ) : (
                    <Button 
                      variant="contained" 
                      color="primary"
                      onClick={handleNextQuestion}
                      disabled={!isQuestionAnswered(filteredQuestions[currentQuestionIndex].id)}
                      size="large"
                      sx={{ px: 4 }}
                    >
                      Next
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          ) : loadingTest ? (
            <Box sx={{ textAlign: 'center', p: 5 }}>
              <CircularProgress size={40} />
              <Typography variant="h6" sx={{ mt: 2 }}>Loading test questions...</Typography>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', p: 5 }}>
              <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>No test questions available for this training.</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseTest} color="primary" size="large">
            Cancel Test
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EmployeeTrainingView;